/**
 * ReadonlyKanbanBoard — superfície compartilhada dos Kanbans somente leitura.
 *
 * Centraliza a arquitetura visual consolidada em Propostas e Contratos:
 * rolagem horizontal indicada, altura estável e bloqueio de vazamento vertical.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-26 v1.0.0 - Extração da base compartilhada.
 */
import type { ReactNode, WheelEvent } from 'react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import { cn } from '@/lib/utils';

interface ReadonlyKanbanBoardProps {
  children: ReactNode;
  className?: string;
  trackClassName?: string;
}

/** Superfície horizontal compartilhada por Kanbans sem arrastar e soltar. */
export function ReadonlyKanbanBoard({
  children,
  className,
  trackClassName,
}: ReadonlyKanbanBoardProps) {
  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    // Impede que a roda vertical em uma área vazia mova a página por trás do Kanban.
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) event.stopPropagation();
  };

  return (
    <div
      className={cn(
        'h-full min-h-0 min-w-0 w-full max-w-full overflow-hidden overscroll-contain',
        className,
      )}
      onWheel={handleWheel}
    >
      <ScrollIndicators className="h-full">
        <ScrollArea
          className="h-full w-full max-w-full"
          preventScrollChaining
          touchAxis="pan-x"
        >
          <div className={cn('flex gap-3 p-3 h-[calc(100vh-200px)]', trackClassName)}>
            {children}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </ScrollIndicators>
    </div>
  );
}